import { useMemo, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
// Models
import { IPage } from '@root/models/Page';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { useLazyGetAuthorsQuery } from 'app/store/EpisodeAuthors/EpisodeAuthors.api';
import { getAuthor } from 'app/store/EpisodeAuthors/EpisodeAuthors.async';
// Actions
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
import { EpisodeAuthorsActions } from 'app/store/EpisodeAuthors/EpisodeAuthors.slice';
// Selectors
import { selectEpisodeAuthorSuggestionEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectPageStaples } from 'app/store/page/page.selectors';
// Mui
import { Box, Button, Tooltip, IconButton } from '@mui/material';
// Icons
import {
  Person as PersonIcon,
  ChevronLeftOutlined as ChevronLeftOutlinedIcon,
  ChevronRightOutlined as ChevronRightOutlinedIcon
} from '@mui/icons-material';
// Context
import useAuthorTypeGroupContext from './utils/useAuthorTypeGroupContext';

const MAX_ITEMS_TO_SHOW = 4;

const AuthorSuggestedButtons = () => {
  const dispatch = useAppDispatch();
  // State
  const episodeAuthorSuggestionEnabled = useAppSelector(selectEpisodeAuthorSuggestionEnabled);
  const staplePages = useAppSelector(selectPageStaples);

  const [ trigger ] = useLazyGetAuthorsQuery();

  const { setValue } = useFormContext();
  const { onOpenAuthorAutocomplete, onCloseAuthorAutocomplete, ControlProps } = useAuthorTypeGroupContext();

  const suggestedAuthorNames = useMemo(() => {
    if ( !staplePages || !staplePages.length ) return [];
    return staplePages.reduce((acc:string[], cur:IPage) => {
      if ( cur.annotations && cur.annotations.length ){
        for ( let annotation of cur.annotations ){
          if ( annotation.name !== 'per' || acc.includes(annotation.value) ) continue;
          acc.push(annotation.value);
        }
      }
      return acc;
    }, []);
  }, [staplePages]);

  const hasMoreThanMaxItemsToShow = useMemo(() => {
    return suggestedAuthorNames.length > MAX_ITEMS_TO_SHOW;
  }, [suggestedAuthorNames]);

  const [ selectedAuthorName, setSelectedAuthorName ] = useState<string | null>(null);
  const [ showMore, setShowMore ] = useState(!hasMoreThanMaxItemsToShow);

  useEffect(() => {
    if ( !selectedAuthorName ) return;

    const fetchAuthors = async () => {
      dispatch(EpisodeAuthorsActions.setAuthor(null));
      dispatch(EpisodeAuthorsActions.setAuthors(null));

      const result = await trigger({
        name: selectedAuthorName,
        t: Date.now()
      }).unwrap();

      dispatch(EpisodeAuthorsActions.setAuthors(result));

      if ( result.length > 1 ){
        // Manually open the autocomplete
        onOpenAuthorAutocomplete();
      } else {
        onCloseAuthorAutocomplete();

        const author = result[0] || { name: selectedAuthorName };

        setValue('author', author);

        if ( author.id ){
          dispatch(getAuthor({ authorId: author.id, params: { fields: 'thirdPartyMatches' } }));
        }

        dispatch(MixpanelActions.handleEpisodeSuggestedAuthorClick({
          authorId: author.id,
          authorName: author.name
        }));
      }

      setSelectedAuthorName(null);
    };

    fetchAuthors();

    // eslint-disable-next-line
  }, [selectedAuthorName]);

  const handleClick = (authorName:string) => () => {
    setValue('author', {});
    setSelectedAuthorName(authorName);
  }

  const toggleShowMore = () => {
    setShowMore(!showMore);
  }

  const slicedSuggestedAuthorNames = showMore
    ? suggestedAuthorNames
    : suggestedAuthorNames.slice(0, MAX_ITEMS_TO_SHOW)
  ;

  if ( !episodeAuthorSuggestionEnabled ) return null;
  if ( !suggestedAuthorNames.length || ControlProps?.disabled ) return null;
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      mb: 4
    }}>
      <Tooltip title="Suggested authors">
        <PersonIcon sx={{ color: 'rgba(0,0,0,0.54)' }} />
      </Tooltip>
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2
      }}>
        {slicedSuggestedAuthorNames.map((authorName:string, index:number) => (
          <Button
            key={`authorItem-${index}`}
            sx={{ whiteSpace: 'nowrap', textTransform: 'initial' }}
            onClick={handleClick(authorName)}
            variant="outlined"
          >{authorName}</Button>
        ))}
        {hasMoreThanMaxItemsToShow ? (
          <IconButton
            onClick={toggleShowMore}
            size="small"
          >{showMore ? <ChevronLeftOutlinedIcon /> : <ChevronRightOutlinedIcon />}</IconButton>
        ) : null}
      </Box>
    </Box>
  )
}

export default AuthorSuggestedButtons;
